/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import { Drawer, IconButton } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import { AlertTriangle, Lock, Upload } from 'lucide-react';
import { FieldLabel } from '../subscriptions/components/shared/SharedComponents';

const INPUT_CLASS =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 placeholder-gray-400';
const LOCKED_INPUT_CLASS =
  'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-500 bg-gray-50 outline-none cursor-not-allowed pr-9';

const EMPTY_FORM = {
  mailTemplateType: '',
  templateName: '',
  subject: '',
  htmlTemplate: '',
  attachmentHtmlTemplate: '',
  placeHolderList: '',
};

const MODE_TITLES = {
  create: 'Add Email Template',
  edit: 'Edit Email Template',
  view: 'Email Template',
};

// The type/name pair is what every mail-sending API looks templates up by, so it
// is only ever set at creation time and locked from then on.
const IDENTITY_LOCK_HINT =
  'Locked — the mail-sending APIs look this template up by this value.';

export default function MailTemplateDrawer({ open, mode, record, loading, saving, error, onClose, onSubmit }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [tab, setTab] = useState('html');
  const [fileError, setFileError] = useState(null);
  const fileInputRef = useRef(null);

  const isView = mode === 'view';
  const isCreate = mode === 'create';
  const identityLocked = !isCreate;

  useEffect(() => {
    if (!open) return;
    setTab(isView ? 'preview' : 'html');
    setFileError(null);
    if (isCreate) {
      setForm(EMPTY_FORM);
      return;
    }
    setForm({
      mailTemplateType: record?.mail_template_type || '',
      templateName: record?.template_name || '',
      subject: record?.subject || '',
      htmlTemplate: record?.html_template || '',
      attachmentHtmlTemplate: record?.attachment_html_template || '',
      placeHolderList: record?.place_holder_list || '',
    });
  }, [open, mode, record, isCreate, isView]);

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handleFilePick = (event) => {
    const file = event.target.files?.[0];
    // Reset immediately so picking the same file twice still fires onChange.
    event.target.value = '';
    if (!file) return;
    if (!/\.(html?|txt)$/i.test(file.name)) {
      setFileError('Please choose an .html file.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFileError(null);
      setForm((prev) => ({ ...prev, htmlTemplate: String(reader.result || '') }));
      setTab('preview');
    };
    reader.onerror = () => setFileError('Could not read that file.');
    reader.readAsText(file);
  };

  const handleSubmit = () => {
    if (isView) return;
    const common = {
      subject: form.subject.trim(),
      htmlTemplate: form.htmlTemplate,
      attachmentHtmlTemplate: form.attachmentHtmlTemplate || null,
      placeHolderList: form.placeHolderList || null,
    };
    // Identity fields are omitted on edit — the API rejects any attempt to change them.
    onSubmit(
      isCreate
        ? {
            ...common,
            mailTemplateType: form.mailTemplateType.trim(),
            templateName: form.templateName.trim(),
          }
        : common
    );
  };

  const canSubmit =
    !saving &&
    form.subject.trim() &&
    form.htmlTemplate.trim() &&
    (!isCreate || (form.mailTemplateType.trim() && form.templateName.trim()));

  const LockedField = ({ label, value, hint }) => (
    <div className="mb-4">
      <FieldLabel>{label}</FieldLabel>
      <div className="relative">
        <input type="text" value={value} disabled className={LOCKED_INPUT_CLASS} />
        <Lock className="w-3.5 h-3.5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
      </div>
      <p className="text-xs text-gray-400 mt-1">{hint}</p>
    </div>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: '720px' },
          borderLeft: '1px solid #e5e7eb',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
        <div className="min-w-0">
          <h2 className="text-base font-bold text-gray-900">{MODE_TITLES[mode] || 'Email Template'}</h2>
          {!isCreate && record?.mail_template_type && (
            <p className="text-xs text-gray-400 mt-0.5 truncate">{record.mail_template_type}</p>
          )}
        </div>
        <IconButton size="small" onClick={onClose} sx={{ color: '#6b7280' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {/* ── Body ── */}
      <div className="px-6 py-5 flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <>
            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl">
                <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {identityLocked ? (
              <>
                <LockedField label="Template Type" value={form.mailTemplateType} hint={IDENTITY_LOCK_HINT} />
                <LockedField label="Template Name" value={form.templateName} hint={IDENTITY_LOCK_HINT} />
              </>
            ) : (
              <>
                <div className="mb-4">
                  <FieldLabel required>Template Type</FieldLabel>
                  <input
                    type="text"
                    value={form.mailTemplateType}
                    placeholder="e.g. NUDGE_TEST_INCOMPLETE"
                    onChange={set('mailTemplateType')}
                    className={INPUT_CLASS}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    The key the sending APIs look this template up by. Cannot be changed later.
                  </p>
                </div>
                <div className="mb-4">
                  <FieldLabel required>Template Name</FieldLabel>
                  <input
                    type="text"
                    value={form.templateName}
                    placeholder="e.g. Nudge - Test Incomplete"
                    onChange={set('templateName')}
                    className={INPUT_CLASS}
                  />
                  <p className="text-xs text-gray-400 mt-1">Cannot be changed later.</p>
                </div>
              </>
            )}

            <div className="mb-4">
              <FieldLabel required>Subject</FieldLabel>
              <input
                type="text"
                value={form.subject}
                maxLength={100}
                disabled={isView}
                placeholder="Shown in the recipient's inbox"
                onChange={set('subject')}
                className={isView ? LOCKED_INPUT_CLASS : INPUT_CLASS}
              />
              {/* mail_template.subject is varchar(100) — surface the budget rather
                  than letting the insert fail server-side. */}
              <p className="text-xs text-gray-400 mt-1">{form.subject.length}/100 characters. Plain text — HTML entities do not decode in a mail header.</p>
            </div>

            <div className="mb-4">
              <FieldLabel>Placeholders</FieldLabel>
              <input
                type="text"
                value={form.placeHolderList}
                maxLength={500}
                disabled={isView}
                placeholder="e.g. {{FirstName}}, {{CTALink}}"
                onChange={set('placeHolderList')}
                className={isView ? LOCKED_INPUT_CLASS : INPUT_CLASS}
              />
              <p className="text-xs text-gray-400 mt-1">Tokens the sender replaces at send time.</p>
            </div>

            {/* ── HTML body ── */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex border-b border-gray-200">
                {['html', 'preview'].map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTab(key)}
                    className={`px-4 py-2 text-sm whitespace-nowrap -mb-px cursor-pointer bg-transparent border-0 border-b-2 transition-colors duration-150
                      ${tab === key
                        ? 'font-semibold text-blue-600 border-blue-600'
                        : 'font-normal text-gray-500 border-transparent hover:text-gray-900'}`}
                  >
                    {key === 'html' ? 'HTML' : 'Preview'}
                  </button>
                ))}
              </div>
              {!isView && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1.5 border border-gray-300 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                >
                  <Upload className="w-3.5 h-3.5" /> Upload HTML file
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".html,.htm,text/html"
              onChange={handleFilePick}
              className="hidden"
            />
            {fileError && <p className="text-xs text-red-500 mb-2">{fileError}</p>}

            {tab === 'html' ? (
              <textarea
                rows={16}
                value={form.htmlTemplate}
                disabled={isView}
                placeholder="Paste the email HTML, or use Upload HTML file"
                onChange={set('htmlTemplate')}
                className={`${isView ? LOCKED_INPUT_CLASS : INPUT_CLASS} font-mono text-xs leading-relaxed resize-y`}
              />
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white h-[420px]">
                {form.htmlTemplate.trim() ? (
                  // sandbox with no allow-scripts: template markup renders but
                  // cannot run anything against the admin session.
                  <iframe
                    title="Email template preview"
                    srcDoc={form.htmlTemplate}
                    sandbox=""
                    className="w-full h-full border-0"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-sm text-gray-400">Nothing to preview yet.</p>
                  </div>
                )}
              </div>
            )}

            <div className="mt-4">
              <FieldLabel>Attachment HTML (optional)</FieldLabel>
              <textarea
                rows={3}
                value={form.attachmentHtmlTemplate}
                disabled={isView}
                onChange={set('attachmentHtmlTemplate')}
                className={`${isView ? LOCKED_INPUT_CLASS : INPUT_CLASS} font-mono text-xs resize-none`}
              />
            </div>
          </>
        )}
      </div>

      {/* ── Footer ── */}
      <div className="px-6 py-4 border-t border-gray-100 flex gap-2.5 justify-end flex-shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
        >
          {isView ? 'Close' : 'Cancel'}
        </button>
        {!isView && (
          <LoadingButton
            loading={Boolean(saving)}
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            sx={{
              background: '#2563eb', borderRadius: '7px', textTransform: 'none',
              fontSize: '13px', fontWeight: 600, px: '20px',
              '&:hover': { background: '#1d4ed8' },
            }}
          >
            {isCreate ? 'Create Template' : 'Save Changes'}
          </LoadingButton>
        )}
      </div>
    </Drawer>
  );
}
